import { TypedDocumentNode } from '@apollo/client';
import { useDeepCompareEffect } from 'react-use';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';

import { useGraphQlSseContext } from '../components/GraphqlSseContext/GraphqlSseContext';
import { getGqlString } from '../helpers/getGqlString';

export const useSubscription = <TData, TVariables>({
  query,
  variables,
  onNext,
  operationName,
}: {
  query: TypedDocumentNode<TData, TVariables>;
  variables?: TVariables;
  onNext: (data: TData) => void;
  operationName: string;
}) => {
  const { graphqlSseClient } = useGraphQlSseContext();
  const finalQuery = getGqlString(query) ?? '';

  useDeepCompareEffect(() => {
    const subscription = graphqlSseClient.subscribe(
      {
        query: finalQuery,
        operationName,
        variables: variables || {},
      },
      {
        next: (result: { data: TData }) => {
          onNext(result.data);
        },
        error: (error: Error) => {
          logError(error);
        },
        complete: () => {
          return undefined;
        },
      },
    );

    return () => {
      if (subscription && typeof subscription === 'function') {
        subscription();
      }
    };
  }, [finalQuery, variables, onNext, graphqlSseClient, operationName]);
};
